import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {updateObject} from 'utils/reducerUtils';

import AudioPlayer from 'components/AudioPlayer';

import audioFilesNo from './languages/no-audioFiles';
import audioFilesNn from './languages/nn-audioFiles';
import normingAudioFilesNo from './languages/no-norming-audioFiles';
import normingAudioFilesNn from './languages/nn-norming-audioFiles';

import assignments from './languages/data.json';
import normingAssignments from './languages/normingData.json';
import correctAnswersNo from './languages/no-dld-answers.json';
import correctAnswersNn from './languages/nn-dld-answers.json';

import strings from 'utils/strings';

import {isTablet} from 'react-device-detect';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faVolumeHigh} from '@fortawesome/free-solid-svg-icons';

const audioFilesObj = {
	no: audioFilesNo,
	nn: audioFilesNn,
};

const normingAudioFilesObj = {
	no: normingAudioFilesNo,
	nn: normingAudioFilesNn,
};

const dldCorrectAnswersObj = {
	no: correctAnswersNo,
	nn: correctAnswersNn,
};

const maxAudioPlayCount = 2;
const failureLimit = 6;
const normalizeAnswer = (value) => (value || '').trim().toLowerCase();

class Test extends React.Component {
	constructor(props) {
		super(props);
		this.handleItemVisited = this.handleItemVisited.bind(this);

		const isDldTest = this.props.isDldTest ? 1 : 0;
		const language = strings.getLanguage();
		this.audioFiles = isDldTest
			? normingAudioFilesObj[language]
			: audioFilesObj[language];
		this.testAssessment =
			isDldTest === 1 ? normingAssignments : assignments;
		this.correctAnswers = isDldTest ? dldCorrectAnswersObj[language] : [];

		const results = {};

		for (const assignment of this.testAssessment) {
			results[assignment.id] = assignment.value;
		}

		this.state = {
			results,
			visitedItemIds: [],
			isStopping: false,
			disabledAssignments: {},
			audioPlayCounts: {},
			renderFinalSound: null,
			allowPlayNow: false,
			showWordAudio: false,
			currentClickedIndex: null,
			showChoiceAudio: false,
			currentChoiceIndex: null,
			showQuestionPlayButton: true,
			showChoicePlayButton: true,
		};
	}

	setResults(id, value) {
		const {results} = this.state;

		this.setState({
			results: updateObject(results, {
				[id]: value,
			}),
		});
	}

	isCorrectAnswer(id, answer) {
		const correctAnswer = this.correctAnswers.find((item) => item.id === id);
		const normalizedAnswer = normalizeAnswer(answer);

		if (!correctAnswer || !normalizedAnswer) {
			return false;
		}

		const acceptedAnswers = [
			correctAnswer.value,
			...(correctAnswer.altValues || []),
		];

		return acceptedAnswers.some(
			(value) => normalizeAnswer(value) === normalizedAnswer
		);
	}

	shouldStopTest(visitedItemIds, results) {
		const visitedItems = new Set(visitedItemIds);
		let consecutiveFailures = 0;

		for (const item of this.testAssessment) {
			if (visitedItems.has(item.id)) {
				consecutiveFailures = this.isCorrectAnswer(
					item.id,
					results[item.id]
				)
					? 0
					: consecutiveFailures + 1;
			}
		}

		return consecutiveFailures >= failureLimit;
	}

	handleItemVisited(id, value) {
		if (!this.props.isDldTest || this.state.isStopping) {
			return;
		}

		const results = updateObject(this.state.results, {[id]: value});
		const visitedItemIds = this.state.visitedItemIds.includes(id)
			? this.state.visitedItemIds
			: [...this.state.visitedItemIds, id];
		const isStopping = this.shouldStopTest(visitedItemIds, results);

		this.setState({results, visitedItemIds, isStopping}, () => {
			if (isStopping) {
				this.props.onNextStep(results);
			}
		});
	}

	getDisabledAssignments() {
		const {results} = this.state;

		const disabledAssignments = {};
		for (let i = 0; i < this.testAssessment.length; i++) {
			const assignmentId = this.testAssessment[i].id;
			if (results[assignmentId]) {
				disabledAssignments[assignmentId] = true;
			}
		}
		return disabledAssignments;
	}

	render() {
		const {onNextStep, refs, isSendingTestResults, isDldTest} = this.props;

		const {
			audioPlayCounts,
			disabledAssignments,
			results,
			allowPlayNow,
			isStopping,
		} = this.state;

		const allResultsHasValues = Object.keys(results).every(
			(key) => results[key]
		);
		const isNextButtonVisible = allResultsHasValues;

		return (
			<div
				className={
					isDldTest
						? 'lit-wrapper--thin lit-test lit-diktat lit-norming'
						: 'lit-wrapper--thin lit-test lit-diktat'
				}
			>
				{!isTablet &&
					this.testAssessment.map((assignment) => {
						const assignmentId = assignment.id;
						const audioFile = this.audioFiles[assignmentId];
						const assignmentAudioPlayCount =
							audioPlayCounts[assignmentId] || 0;
						const isDisabled = disabledAssignments[assignmentId];
						const isAudioDisabled =
							isDisabled ||
							assignmentAudioPlayCount === maxAudioPlayCount;
						let lastRow = false;
						if (assignmentId === this.testAssessment.length) {
							lastRow = true;
						}

						let audioPlayerRef;
						return (
							<div
								className={classNames(
									'lit-input lit-revealed',
									{
										'lit-input--disabled': isDisabled,
									}
								)}
								key={assignmentId}
							>
								<AudioPlayer
									disabled={isAudioDisabled}
									id={assignmentId}
									onStart={() => {
										this.setState({
											audioPlayCounts: updateObject(
												audioPlayCounts,
												{
													[assignmentId]:
														assignmentAudioPlayCount +
														1,
												}
											),
										});
									}}
									ref={(ref) => {
										audioPlayerRef = ref;
									}}
									refs={refs}
									src={audioFile}
									onErrorPlay={() => {
										console.log('Something went wrong');
										this.setState({
											showQuestionPlayButton: true,
											currentClickedIndex: null,
											showChoicePlayButton: true,
											audioPlayCounts: updateObject(
												audioPlayCounts,
												{
													[assignmentId]:
														assignmentAudioPlayCount -
														1,
												}
											),
										});
									}}
									onPause={() => {
										if (lastRow && allowPlayNow) {
											this.setState({
												renderFinalSound: true,
											});
										}
									}}
								/>
								<input
									className="lit-input__field"
									disabled={isDisabled}
									onChange={(ev) => {
										if (isDisabled) {
											return;
										}
										const value = ev.target.value;
										this.setResults(assignmentId, value);
									}}
									onBlur={(ev) =>
										this.handleItemVisited(
											assignmentId,
											ev.target.value
										)
									}
									onFocus={() => {
										const disabledAssignments =
											this.getDisabledAssignments();
										disabledAssignments[
											assignmentId
										] = false;

										if (assignmentAudioPlayCount === 0) {
											audioPlayerRef.handleStartPlayer();
										}

										this.setState({
											disabledAssignments,
										});
									}}
									placeholder={strings.writeWordHere}
									spellCheck={false}
									type="text"
								/>
							</div>
						);
					})}

				{isTablet &&
					this.testAssessment.map((assignment, index) => {
						const assignmentId = assignment.id;
						const mainIndex = index + 1;
						const audioFile = this.audioFiles[assignmentId];
						const assignmentAudioPlayCount =
							audioPlayCounts[assignmentId] || 0;
						let lastRow = false;
						if (assignmentId === this.testAssessment.length) {
							lastRow = true;
						}
						const isDisabled = disabledAssignments[assignmentId];
						const isAudioDisabled =
							isDisabled ||
							assignmentAudioPlayCount === maxAudioPlayCount;

						let audioPlayerRef;
						return (
							<div
								style={{
									display: 'flex',
									marginTop: '50px',
								}}
								className={classNames(
									'lit-input lit-revealed',
									{
										'lit-input--disabled': isDisabled,
									}
								)}
								key={assignmentId}
							>
								<div
									className=""
									style={{
										height: '50px',
										width: '50px',
										marginRight: '20px',
									}}
								>
									{this.state.showQuestionPlayButton && (
										<FontAwesomeIcon
											size="lg"
											className={
												!isAudioDisabled
													? 'lit-play-button-ordiktat'
													: 'lit-play-button-ordiktat-disabled '
											}
											icon={faVolumeHigh}
											onClick={() => {
												if (!isAudioDisabled) {
													this.setState({
														showWordAudio: true,
														showQuestionPlayButton: false,
														currentClickedIndex:
															mainIndex,
														showChoicePlayButton: false,
													});
												}
											}}
										/>
									)}
									{!this.state.showQuestionPlayButton &&
										this.state.currentClickedIndex !==
											mainIndex && (
											<FontAwesomeIcon
												size="lg"
												className="lit-play-button-ordiktat-disabled"
												icon={faVolumeHigh}
											/>
										)}

									{this.state.showWordAudio &&
										this.state.currentClickedIndex ===
											mainIndex && (
											<div>
												<AudioPlayer
													disabled={isAudioDisabled}
													onErrorPlay={() => {
														console.log(
															'Something went wrong'
														);
														this.setState({
															showQuestionPlayButton: true,
															currentClickedIndex:
																null,
															showChoicePlayButton: true,
															audioPlayCounts:
																updateObject(
																	audioPlayCounts,
																	{
																		[assignmentId]:
																			assignmentAudioPlayCount -
																			1,
																	}
																),
														});
													}}
													id={assignmentId}
													autoplay
													onComplete={() => {
														this.setState({
															showQuestionPlayButton: true,
															currentClickedIndex:
																null,
															showChoicePlayButton: true,
														});
													}}
													onPause={() => {
														if (
															lastRow &&
															allowPlayNow
														) {
															this.setState({
																renderFinalSound: true,
															});
														}
													}}
													onStart={() => {
														this.setState({
															audioPlayCounts:
																updateObject(
																	audioPlayCounts,
																	{
																		[assignmentId]:
																			assignmentAudioPlayCount +
																			1,
																	}
																),
														});
													}}
													ref={(ref) => {
														audioPlayerRef = ref;
													}}
													refs={refs}
													src={audioFile}
												/>
											</div>
										)}
								</div>
								<input
									className="lit-input__field"
									disabled={isDisabled}
									spellCheck={false}
									autoComplete="off"
									autoCorrect="off"
									autoCapitalize="off"
									id={`tf${index + 1}`}
									onKeyDown={(event) => {
										if (
											event.key === 'Enter' &&
											index < this.testAssessment.length - 1
										) {
											document
												.getElementById(
													`tf${index + 2}`
												)
												.focus();
										} else if (
											event.key === 'Enter' &&
											lastRow &&
											allResultsHasValues
										) {
											this.setState({
												allowPlayNow: true,
												renderFinalSound: true,
											});
										}
									}}
									onChange={(ev) => {
										if (isDisabled) {
											return;
										}
										const value = ev.target.value;

										this.setResults(assignmentId, value);
									}}
									onBlur={(ev) =>
										this.handleItemVisited(
											assignmentId,
											ev.target.value
										)
									}
									onFocus={() => {
										const disabledAssignments =
											this.getDisabledAssignments();
										disabledAssignments[
											assignmentId
										] = false;

										/* if (assignmentAudioPlayCount === 0) {
										audioPlayerRef.handleStartPlayer();
									} */
										if (!isAudioDisabled) {
											this.setState({
												showWordAudio: true,
												showQuestionPlayButton: false,
												currentClickedIndex: mainIndex,
												showChoicePlayButton: false,
											});
										}

										this.setState({
											disabledAssignments,
										});
									}}
									placeholder={strings.writeWordHere}
									type="text"
								/>
							</div>
						);
					})}
				{isNextButtonVisible && (
					<button
						className="lit-btn lit bg-primary"
						onClick={() => {
							if (!this.state.isStopping) {
								onNextStep(this.state.results);
							}
						}}
						disabled={isSendingTestResults || isStopping}
					>
						{strings.done}
					</button>
				)}
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const {
		testStepAnswers: {isSendingTestResults},
	} = state;

	return {
		isSendingTestResults,
	};
};

export default connect(mapStateToProps)(Test);
